import dayjs from "dayjs"; // Importing dayjs for date manipulation
import { Field, Form, Formik, useFormikContext } from "formik"; // Importing Formik components for form handling
import { observer } from "mobx-react"; // Importing observer for MobX state management
import moment from "moment"; // Importing moment for date and time manipulation
import { Button, Modal } from "react-bootstrap"; // Importing Bootstrap components for UI
import { FormattedMessage } from "react-intl"; // Importing FormattedMessage for internationalization
import { getStoreIdFromLocalStorage } from "../../../../helpers/localstorage-helper"; // Helper function to get store ID from local storage
import { IOption } from "../../../../models/ICommon"; // Importing IOption interface
import LoaderButton from "../../../../shared-components/button/loader-button"; // Custom loader button component
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput"; // Custom Formik input component
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect"; // Custom Formik select component
import FormikFormStaffBreakFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikFormStaffBreakFieldMUI"; // Custom Formik time picker component
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea"; // Custom Formik textarea component
import { AddStaffBreakValidationSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema"; // Validation schema for the form
import FormLoader from "../../../../shared-components/FormLoader/FormLoader"; // Loader component for forms
import Label from "../../../../shared-components/label/label"; // Custom label component
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup"; // Delete confirmation popup
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc"; // Higher-order component for delete popup
import { formatMessage } from "../../../../translations/format-message"; // Helper function for message formatting
import { useEffect } from "react";
import FormikFormTimePicker from "../../../../shared-components/FormikFormHandlers/FormikFormTimePicker";

interface IProps {
  id: number; // ID of the break
  isLoading: boolean; // Loading state
  day: any;
  staffList: IOption[]; // List of staff options
  staffId: number; // Selected staff ID
  activeTabId: any; // Active tab ID
  startTime: string; // Start time of the break
  showDeletePopup: boolean; // Flag to show delete popup
  submitHandler: (data: any) => void; // Submit handler function
  onModalClosed: () => void; // Function to handle modal close
  deletePopupToggleHandler: () => void; // Function to toggle delete popup
  handleDeleteSubmitHandler: () => void; // Function to handle delete submission
  onDeleteModalClosed: () => void; // Function to handle delete modal close
  initialValues: any; // Initial values for the form
  viewOnly: boolean; // Flag for view-only mode
}

const AddBreakDetailsFormComponent = (props: IProps) => {
  const breakInitialValues = {
    Id: -1, // Default ID for a new break
    Title: "Lunch Break", // Default title
    Date: localStorage.getItem("currentDate"), // Current date from local storage
    StartTime: dayjs(
      `${localStorage.getItem("currentDate")}T${props.startTime}`
    ), // Start time with current date
    EndTime: "", // Default end time
    StaffId: Number(props.staffId), // Selected staff ID
    StoreId: getStoreIdFromLocalStorage(), // Store ID from local storage
    TotalTime: 0, // Default total time
    Remarks: "", // Default remarks
  };

    const FormObserver: React.FC = () => {
      const { values, setFieldValue } = useFormikContext<any>();

      useEffect(() => {
        if(props.activeTabId == "1" && props.id > 0 && props.initialValues?.BreakType == 0)  
        setFieldValue("Title", "");
        
      }, [props.activeTabId]);

      return null;
    };

  const validateTime = (endTime: any) => {
    const startTime = props.startTime; // Start time from props
    const selectedDate = localStorage.getItem("currentDate"); // Current date from local storage
    let error;

    if (!endTime || !endTime.$d) {
      error = "field_required"; // Error if end time is not provided
    } else if (endTime.$d.toString() === "Invalid Date") {
      error = "invalid_time_format"; // Error if end time format is invalid
    } else if (startTime && selectedDate) {
      const formattedDate = moment(selectedDate).format("YYYY-MM-DD"); // Format selected date
      const start = moment(`${formattedDate} ${startTime}`, "YYYY-MM-DD HH:mm"); // Parse start time

      // Convert endTime.$d to "HH:mm" string & re-parse like start
      const endFormatted = moment(endTime.$d).format("HH:mm"); // Format end time
      const end = moment(
        `${formattedDate} ${endFormatted}`,
        "YYYY-MM-DD HH:mm"
      ); // Parse end time

      if (end.isSameOrBefore(start)) {
        error = "end_time_must_be_after_start_time"; // Error if end time is before or same as start time
      }
    }

    return error; // Return validation error
  };

  const handleDelete = () => {
    props.deletePopupToggleHandler(); // Toggle delete popup
  };

  const handleDeleteModalClosed = () => {
    props.deletePopupToggleHandler(); // Toggle delete popup on modal close
  };
  return (
    <>
      {props.showDeletePopup && (
        <DeletePopup
          modalSubmit={props.handleDeleteSubmitHandler} // Submit handler for delete popup
          modalClosed={handleDeleteModalClosed} // Close handler for delete popup
          isLoading={false} // Loading state for delete popup
          title={formatMessage("delete_confirmation")} // Title for delete popup
          message={formatMessage("are_you_sure_you_want_to_delete_this_break")} // Message for delete popup
        />
      )}

      <Formik
        initialValues={{
          ...props.initialValues,
          EndTime: moment(props.initialValues.EndTime).format("HH:mm"),
          StartTime: moment(props.initialValues.StartTime).format("HH:mm"),
        }} // Initial values for the form
        enableReinitialize // Reinitialize form on prop changes
        validateOnBlur={false} // Disable validation on blur
        validationSchema={AddStaffBreakValidationSchema} // Validation schema for the form
        onSubmit={async (values) => {
          props.submitHandler({...values, Title: props.activeTabId === "0" ? "Break" : values.Title}); // Submit handler for the form
        }}
      >
        {({ setFieldValue, values }) => (
          <Form className="user-form">
            {props.isLoading && <FormLoader loading={props.isLoading} />}{" "}
            {/* Loader for the form */}
            <FormObserver />
            <Modal.Body>
              <div className="row">
                <div className="col-sm-12 form-group">
                  <Label
                    className="label-style"
                    required={true} // Mark label as required
                    label="title" // Label for title field
                  />

                {props.activeTabId === "0" ? 
                  <Field
                  className="form-control"
                  name="Title" // Name for title field
                  type="text"
                  value={formatMessage("break")} // Set value to "Break"
                  disabled={props.viewOnly || props.activeTabId == "0"} // Disable title field if viewOnly or editing existing break
                  placeholder="title" // Placeholder for title field
                  as={FormikFormInput} // Custom Formik input component
                  />
                  : 
                  <Field
                    className="form-control"
                    name="Title" // Name for title field
                    type="text"
                    
                    placeholder="title" // Placeholder for title field
                    as={FormikFormInput} // Custom Formik input component
                  />
                  
                }
                </div>
                
                <div className="col-sm-12 form-group">
                  <Label
                    className="label-style"
                    required={true} // Mark label as required
                    label="staff" // Label for staff field
                  />
                  <Field
                    name="StaffId" // Name for staff field
                    as={FormikFormSelect} // Custom Formik select component
                    options={props.staffList} // Options for staff select
                    disabled={
                      props.viewOnly
                        ? props.viewOnly
                        : props.day?.props["data-role"] == "weekly"
                        ? false
                        : true
                    } // Disable staff select
                  />
                </div>
                <div className="col-sm-6 form-group">
                  <Label
                    className="label-style"
                    required={true} // Mark label as required
                    label="start_time" // Label for start time field
                  />
                  <Field
                    name="StartTime" // Name for start time field
                    as={FormikFormTimePicker} // Custom Formik time picker component
                    className="form-control"
                    disabled={true} // Disable start time field
                  />
                </div>
                <div className="col-sm-6 form-group">
                  <Label
                    className="label-style"
                    required={true} // Mark label as required
                    label="end_time" // Label for end time field
                  />
                  <Field
                    name="EndTime" // Name for start time field
                    as={FormikFormTimePicker} // Custom Formik time picker component
                    className="form-control"
                    disabled={props.viewOnly} // Disable start time field
                    minuteInterval={5} // Set minute interval for time picker
                  />
                </div>

                <div className="form-group">
                  <Label className="label-style" label="remarks" />{" "}
                  {/* Label for remarks field */}
                  <Field
                    name="Remarks" // Name for remarks field
                    as={FormikFormTextArea} // Custom Formik textarea component
                    className="form-control"
                    disabled={props.viewOnly} // Disable remarks field
                    placeholder="remarks" // Placeholder for remarks field
                  />
                  <p className="small text-muted text-end mt-1">{`${
                    values?.Remarks ? values?.Remarks?.length : 0
                  }/256`}</p>{" "}
                  {/* Character count for remarks */}
                </div>
              </div>
            </Modal.Body>
            {/* Footer Buttons */}
            <Modal.Footer className="justify-content-center gap-2">
              <Button
                variant="outline-danger"
                type="button"
                onClick={props.onModalClosed} // Close modal handler
                disabled={props.isLoading} // Disable button if loading
              >
                <i className="bi bi-x-lg me-1"></i>
                <FormattedMessage id="close" /> {/* Close button label */}
              </Button>
              {!props.viewOnly && (
                <>
                  {props.id > 0 && (
                    <Button
                      variant="danger"
                      type="button"
                      onClick={handleDelete} // Delete handler
                      disabled={props.isLoading} // Disable button if loading
                    >
                      <i className="bi bi-trash3-fill me-1"></i>
                      <FormattedMessage id="button_delete" />{" "}
                      {/* Delete button label */}
                    </Button>
                  )}
                  <LoaderButton
                    type="submit"
                    iconName="bi-check-lg" // Icon for submit button
                    isLoading={props.isLoading} // Loading state for submit button
                    text={props.id > 0 ? "button_update" : "add"} // Text for submit button
                  />
                </>
              )}
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default DeletePopupHOC(observer(AddBreakDetailsFormComponent)); // Exporting component wrapped with HOC and observer
